import { defineStore } from 'pinia';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { reactive } from 'vue';
import { IBusinessRepository } from '@/businessRepository/interfaces/businessRepository';
import { openFailToAcquireModal } from '@/corePagesModule/components/modal/modalActions';
import { simpleCopy } from '@/utils/deepCopy';
import { GetAllBusinessUseCase } from '@/businessUseCases/getAllBusinessUseCase';
import { IRouterInfo } from '@/coreDomainsModule/entities/routerInfo';
import { SupplierBusinessInfoService } from '../domains/service/supplierBusinessInfoService';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IBusinessProgressRateInfo } from '@/corePagesModule/progressRateTableContent/domains/entities/businessProgressRateInfo';
import { ProgressRateInfoService } from '@/corePagesModule/progressRateTableContent/domains/service/progressRateInfoService';
import { achieveBusinessCountForTargetYearMonth, totalBusinessResult } from '@/coreDomainsModule/businessTotal';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';

/**
 * 在庫管理画面のStoreを管理する
 */
export const useSupplierForecastStore = defineStore('supplierForecast', () => {
  const state = reactive<ISupplierForecastState>({
    selectYearMonthInfo: { year: 0, month: 0 },
    routerInfo: [],
    achieveCount: 0,
    totalCount: 0,
    useBusinessList: [],
    displayUserBusinessList: [],
    displaySupplierBusinessList: [],
    totalNonResponsiveBusinessCount: 0,
    achieveNonResponsiveBusinessCount: 0,
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.useBusinessList = [];
    state.displayUserBusinessList = [];
    state.displaySupplierBusinessList = [];
    state.selectYearMonthInfo = {
      year: 0,
      month: 0,
    };
    state.routerInfo = [];
    state.achieveCount = 0;
    state.totalCount = 0;
    state.totalNonResponsiveBusinessCount = 0;
    state.achieveNonResponsiveBusinessCount = 0;
  };

  /**
   * stateのFetchと初期化を行う
   * @param businessRepository - 取引情報の永続化の責務を負うクラス
   */
  const fetchState = async (businessRepository: IBusinessRepository) => {
    // 処理が軽いやつら
    // 年月選択UIの初期値
    state.selectYearMonthInfo = DateService.convertDateToIYearMonth(new Date());

    // convertDateToIYearMonthで、Date→IYearMonthに直す際に月情報に+1しているが、
    // MonthPickerではIYearMonthと同じ型の月情報が0~11で表されるため、-1して調整する必要がある
    state.selectYearMonthInfo.month = state.selectYearMonthInfo.month;

    // タブに表示する項目名と遷移先pathの組み合わせ
    state.routerInfo = [
      { router: '/supplier', name: undefined, label: '担当者別' },
      { router: '', name: undefined, label: '仕入先内示別' },
      { router: '', name: undefined, label: '検索キーワード別' },
    ];

    // 処理が重めのやつら
    const tableData = await _getTableData(businessRepository);
    if (tableData == null) return;
    // 取引情報一覧
    state.useBusinessList = tableData;

    // 担当者追加機能が無いので、とりあえずランダムで担当者を付与する形で実装
    // TODO: 担当者登録機能周りができたら削除
    for (const business of state.useBusinessList) {
      if (SupplierBusinessInfoService.randomTrueFalse()) {
        await SupplierBusinessInfoService.addSupplierForecastResponsibleUserName(
          business,
          await SupplierBusinessInfoService.randomLetterIfTrue()
        );
      } else {
        await SupplierBusinessInfoService.addSupplierForecastResponsibleUserName(
          business,
          UserAccountFactory.create({ email: 'hoge@example.com', name: '' })
        );
      }
    }

    // テーブル表示情報の作成
    await updateTableData();
  };

  /**
   * テーブル情報の更新
   */
  const updateTableData = async () => {
    // 全件取引一覧の達成割合
    state.totalCount = await totalBusinessResult(state.useBusinessList);
    state.achieveCount = await achieveBusinessCountForTargetYearMonth(state.useBusinessList, state.selectYearMonthInfo);

    // 担当者未登録の取引のみをフィルターする
    // NOTE: IBusinessの型が変わってエラーが出るのでコメントアウト 25/3/19 武井
    const filteredUnResponsibleUserNameBusiness = state.useBusinessList; /**.filter(
      (ent) => !ent.responsibleUserName || ent.responsibleUserName.supplierForecast === 'ハズレ'
    );*/ // 担当者未登録取引の達成割合
    state.totalNonResponsiveBusinessCount = await totalBusinessResult(filteredUnResponsibleUserNameBusiness);
    state.achieveNonResponsiveBusinessCount = await achieveBusinessCountForTargetYearMonth(
      filteredUnResponsibleUserNameBusiness,
      state.selectYearMonthInfo
    );

    // 担当者別進捗状況を取得
    state.displayUserBusinessList = await ProgressRateInfoService.createBusinessProgressRateInfoByUserName(
      state.useBusinessList,
      state.selectYearMonthInfo
    );
  };

  /**
   * 選択
   */
  const checkBusiness = async () => {
    state.displaySupplierBusinessList = await ProgressRateInfoService.createBusinessProgressRateInfoForSupplierName(
      state.useBusinessList,
      state.displayUserBusinessList[0].businessIds,
      [state.displayUserBusinessList[0].userName],
      state.selectYearMonthInfo
    );
  };

  // #region private関数

  /**
   * TableDataをBusinessManagerから抽出する
   * @param businessRepository - 取引情報の永続化の責務を負うクラス
   */
  const _getTableData = async (businessRepository: IBusinessRepository): Promise<IBusiness[] | null> => {
    // loading中はデータを取得するまで待機する
    const result = await GetAllBusinessUseCase.Execute(businessRepository);

    switch (result.status.type) {
      case 'success':
        const businessArray = simpleCopy(result.status.data);
        return businessArray;
      case 'error':
      default:
        // モーダルでエラーを通知
        await openFailToAcquireModal();
        return null;
    }
  };

  // #endregion

  return {
    state,
    $reset,
    fetchState,
    updateTableData,
    checkBusiness,
  };
});

/**
 * 在庫管理画面のState
 */
interface ISupplierForecastState {
  useBusinessList: IBusiness[];
  displayUserBusinessList: IBusinessProgressRateInfo[];
  displaySupplierBusinessList: IBusinessProgressRateInfo[];
  selectYearMonthInfo: IYearMonth;
  routerInfo: IRouterInfo[];
  achieveCount: number;
  totalCount: number;
  totalNonResponsiveBusinessCount: number;
  achieveNonResponsiveBusinessCount: number;
}
